<template>
  <div class="plan-verification-page">
    <div v-if="checkoutCancelAlert" class="container pt-3">
      <b-alert show dismissible variant="warning" @dismissed="checkoutCancelAlert = false">
        Pago cancelado. Puedes intentarlo de nuevo cuando quieras.
      </b-alert>
    </div>
    <!-- Hero Section - desde DetallesDelPlan -->
    <section
      class="hero-section hero-animate"
      :style="{ backgroundImage: `url(${heroImage})` }"
    >
      <div class="container">
        <div class="row">
          <div class="col-lg-8 col-md-10">
            <h1 class="hero-title">
              Planes que protegen lo que más importa: tu salud y la de tu familia
            </h1>
            <p class="hero-subtitle">
              Accede a consultas médicas ilimitadas, telemedicina 24/7, descuentos en laboratorios y medicamentos.
              Sin copagos, sin complicaciones. Miles de familias ya cuidan su bienestar con nosotros.
            </p>
            <router-link to="/detalles-del-plan" class="btn btn-hero-cta">
              Detalles del plan
              <i class="fas fa-arrow-right ml-2"></i>
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Planes 2027 (mockup: 5 tabs + anual) -->
    <PlansTabsSection :api-plans="plans" />

    <!-- Sección 3 planes con imágenes -->
    <section v-if="!loading && plans.length >= 3" class="plans-carousel-section">
      <div class="container">
        <div class="row align-items-stretch">
          <div
            v-for="(plan, index) in firstThreePlans"
            :key="plan.id"
            class="col-lg-4 col-md-6 mb-4 d-flex"
          >
            <div class="plan-image-card ddp-visible fade-in-up w-100" :style="`transition-delay: ${index * 0.08}s`">
              <div class="plan-image-top plan-image-clickable" @click="$router.push('/detalles-del-plan')">
                <img :src="getPlanCarouselImage(index)" :alt="plan.name" class="plan-image" />
                <div class="plan-image-overlay">
                  <a href="#planes-2027" class="btn btn-lo-necesito" @click.stop>Lo necesito ahora <i class="fas fa-arrow-right ml-1"></i></a>
                </div>
              </div>
              <div class="plan-card-body">
                <h3 class="plan-name">{{ plan.name }}</h3>
                <div class="plan-price-large" :class="{ 'price-secondary': index === 0 || index === 2 }">${{ plan.price }}/Mes</div>
                <div v-if="index === 0" class="plan-savings-line">
                  <span class="plan-original">$90</span>
                  <span class="plan-percent">36% off</span>
                </div>
                <div v-else-if="index === 2" class="plan-savings-line">
                  <span class="plan-original">$140</span>
                  <span class="plan-percent">34% off</span>
                </div>
                <div v-else class="plan-savings-spacer" aria-hidden="true">&nbsp;</div>
                <a href="#planes-2027" class="btn btn-plan-available">
                  <img :src="disponibleIcon" alt="" class="btn-disponible-icon" />
                  Disponible
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Loading State -->
    <section v-if="loading" class="plans-section">
      <div class="container">
        <div class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p class="mt-3">Cargando planes...</p>
        </div>
      </div>
    </section>

    <!-- Plans Comparison Section -->
    <section v-if="!loading && plans && plans.length > 0" class="plans-section">
      <div class="container">
        <div class="section-header text-center mb-5 animate-on-scroll fade-in-up">
          <h2 class="section-title">Comparación de Planes</h2>
          <p class="section-subtitle">
            Comparación detallada de servicios médicos cubiertos
          </p>
        </div>
        
        <div class="table-responsive">
          <table class="table plans-comparison-table">
            <thead>
              <tr>
                <th class="benefit-column">Beneficio / Plan</th>
                <th v-for="plan in plans" :key="plan.id" class="plan-column">
                  <div class="plan-header-cell">
                    <h4>{{ plan.name }}</h4>
                    <div class="plan-price">${{ plan.price }}/month</div>
                    <div class="plan-validity" v-if="plan.validity">
                      <i class="fas fa-calendar-alt mr-1"></i>
                      <small>{{ plan.validity }} meses</small>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="benefit-name">
                  <i class="fas fa-tag mr-2"></i>
                  Tipo de Plan
                </td>
                <td v-for="plan in plans" :key="plan.id" class="text-center">
                  <span class="badge badge-primary badge-lg">
                    {{ plan.membership_type === 0 ? 'Membresía' : plan.membership_type === 1 ? 'Plan Médico' : 'N/A' }}
                  </span>
                </td>
              </tr>
              <tr>
                <td class="benefit-name">
                  <i class="fas fa-clock mr-2"></i>
                  Validez
                </td>
                <td v-for="plan in plans" :key="plan.id" class="text-center">
                  <span class="validity-badge">
                    {{ plan.validity }} meses
                  </span>
                </td>
              </tr>
              <tr>
                <td class="benefit-name">
                  <i class="fas fa-building mr-2"></i>
                  Clínicas
                </td>
                <td v-for="plan in plans" :key="plan.id" class="text-center">
                  <div class="info-compact">
                    <div class="info-number">
                      {{ plan.facilities ? plan.facilities.length : 0 }}
                    </div>
                    <div class="info-label">clínicas</div>
                    <button 
                      class="btn btn-sm btn-link p-0 mt-1" 
                      @click="showFacilitiesModal(plan)"
                      v-if="plan.facilities && plan.facilities.length > 0"
                    >
                      <i class="fas fa-eye mr-1"></i>
                      Ver Todas
                    </button>
                  </div>
                </td>
              </tr>
              <tr>
                <td class="benefit-name">
                  <i class="fas fa-gift mr-2"></i>
                  Beneficios
                </td>
                <td v-for="plan in plans" :key="plan.id" class="text-center">
                  <div class="info-compact">
                    <div class="info-number">
                      {{ plan.parameters_details ? plan.parameters_details.length : 0 }}
                    </div>
                    <div class="info-label">tipos incluidos</div>
                    <button 
                      class="btn btn-sm btn-link p-0 mt-1" 
                      @click="showProceduresModal(plan)"
                      v-if="plan.parameters_details && plan.parameters_details.length > 0"
                    >
                      <i class="fas fa-eye mr-1"></i>
                      Ver Detalles
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- Empty State -->
    <section v-if="!loading && (!plans || plans.length === 0)" class="plans-section">
      <div class="container">
        <div class="text-center py-5">
          <p class="text-muted">No hay planes disponibles en este momento.</p>
          <p v-if="error" class="text-danger mt-2">{{ error }}</p>
        </div>
      </div>
    </section>

    <!-- Detailed Plans Section -->
    <section v-if="!loading && plans && plans.length > 0" class="detailed-plans-section">
      <div class="container-fluid px-3 px-xl-4">
        <div class="section-header text-center mb-5 animate-on-scroll fade-in-up">
          <h2 class="section-title">Detalles del Plan</h2>
          <p class="section-subtitle">
            Información detallada de cada plan disponible
          </p>
        </div>
        <div class="plans-five-row">
          <div
            v-for="(plan, index) in plans"
            :key="plan.id"
            class="plans-five-col"
          >
            <PlanDetailCard
              :plan="plan"
              :index="index"
              :is-visible="true"
              variant="mockup"
              @show-facilities="showFacilitiesModal"
              @show-procedures="showProceduresModal"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Service Areas Section -->
    <ServiceAreasSection 
      :locations="locations" 
      :google-maps-url="googleMapsUrl"
    />

    <!-- Planes y Cobertura - sección original al final -->
    <section
      class="plans-intro-section plans-intro-hero"
      :style="{ backgroundImage: `url(${heroBackgroundImage})` }"
    >
      <div class="container">
        <div class="row">
          <div class="col-lg-8 text-left">
            <h2 class="plans-intro-title">Planes y Cobertura</h2>
            <p class="plans-intro-subtitle">
              Ofrecemos niveles de planes médicos diseñados para adaptarse a las necesidades 
              de cada persona, familia o empresa.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Modal Clínicas / Red de atención -->
    <b-modal
      :id="facilitiesModalId"
      size="lg"
      centered
      hide-header
      body-class="detail-modal-body detail-modal-body--facilities"
      footer-class="detail-modal-footer"
      content-class="detail-modal-content"
      @hidden="selectedPlan = null"
    >
      <template #default>
        <div class="detail-modal">
          <div class="detail-modal__header detail-modal__header--primary">
            <div class="detail-modal__icon-wrap">
              <i class="fas fa-building"></i>
            </div>
            <h3 class="detail-modal__title">
              {{ facilitiesModalTitle }}
            </h3>
            <p class="detail-modal__subtitle">
              Red de clínicas donde puedes usar tu plan <strong v-if="selectedPlan">{{ selectedPlan.name }}</strong>
            </p>
          </div>
          <div class="detail-modal__body">
            <div v-if="selectedPlan && selectedPlan.facilities && selectedPlan.facilities.length > 0" class="facility-list">
              <div
                v-for="(facility, idx) in selectedPlan.facilities"
                :key="idx"
                class="facility-row facility-row--animate"
                :style="{ animationDelay: (idx * 0.04) + 's' }"
              >
                <span class="facility-row__num">{{ idx + 1 }}</span>
                <i class="fas fa-map-marker-alt facility-row__icon"></i>
                <span class="facility-row__name">{{ facility }}</span>
              </div>
            </div>
            <div v-else class="detail-modal__empty">
              <div class="detail-modal__empty-icon">
                <i class="fas fa-hospital"></i>
              </div>
              <p class="detail-modal__empty-title">Sin clínicas asignadas</p>
              <p class="detail-modal__empty-text">Este plan no tiene clínicas configuradas aún. Consulta con nosotros para más información.</p>
            </div>
          </div>
        </div>
      </template>
      <template #modal-footer>
        <b-button class="detail-modal__btn-close" variant="primary" @click="closeFacilitiesModal">
          Entendido
        </b-button>
      </template>
    </b-modal>

    <!-- Modal Procedimientos incluidos -->
    <b-modal
      :id="proceduresModalId"
      size="lg"
      centered
      hide-header
      body-class="detail-modal-body detail-modal-body--procedures"
      footer-class="detail-modal-footer"
      content-class="detail-modal-content"
      @hidden="selectedPlan = null"
    >
      <template #default>
        <div class="detail-modal">
          <div class="detail-modal__header detail-modal__header--secondary">
            <div class="detail-modal__icon-wrap">
              <i class="fas fa-gift"></i>
            </div>
            <h3 class="detail-modal__title">
              {{ proceduresModalTitle }}
            </h3>
            <p class="detail-modal__subtitle">
              Beneficios incluidos en tu cobertura
            </p>
          </div>
          <div class="detail-modal__body detail-modal__body--scroll">
            <ul
              v-if="selectedPlan && selectedPlan.marketingBenefits && selectedPlan.marketingBenefits.length"
              class="marketing-benefits-list"
            >
              <li v-for="(b, idx) in selectedPlan.marketingBenefits" :key="'mkt-' + idx">
                <b>{{ b[0] }}</b>
                <span>{{ b[1] }}</span>
              </li>
            </ul>
            <div v-else-if="selectedPlan && selectedPlan.parameters_details && selectedPlan.parameters_details.length > 0" class="benefits-grid">
              <div
                v-for="(param, idx) in selectedPlan.parameters_details"
                :key="idx"
                class="benefit-card benefit-card--animate"
                :style="{ animationDelay: (idx * 0.05) + 's' }"
              >
                <div class="benefit-card__icon-wrap">
                  <i class="fas fa-check-circle"></i>
                </div>
                <div class="benefit-card__content">
                  <span class="benefit-card__name">
                    {{ (param.parametrizable && param.parametrizable.name) || (param.memberships_parameters_types && param.memberships_parameters_types.name) || 'Beneficio' }}
                  </span>
                  <span class="benefit-card__qty">{{ param.count }} incl.</span>
                  <span v-if="param.memberships_parameters_types && param.memberships_parameters_types.name" class="benefit-card__tag">
                    {{ param.memberships_parameters_types.name }}
                  </span>
                </div>
              </div>
            </div>
            <div v-else class="detail-modal__empty">
              <div class="detail-modal__empty-icon">
                <i class="fas fa-clipboard-list"></i>
              </div>
              <p class="detail-modal__empty-title">Sin beneficios definidos</p>
              <p class="detail-modal__empty-text">La cobertura de beneficios se actualiza periódicamente. Contáctanos para detalles al día.</p>
            </div>
          </div>
        </div>
      </template>
      <template #modal-footer>
        <b-button class="detail-modal__btn-close" variant="primary" @click="closeProceduresModal">
          Entendido
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { BModal, BButton, BAlert } from 'bootstrap-vue'
import plansService from '@/core/services/api/landings/plans'
import { PlanDetailCard, ServiceAreasSection, PlansTabsSection } from '../components'
import { enrichAndOrderHmpPlans } from '../data/hmpPlans2027'

/** Mapeo de imágenes para la sección de 3 planes (por índice) */
const CAROUSEL_IMAGES = [
  require('@/assets/lp/plans/1.png'),
  require('@/assets/lp/plans/2.png'),
  require('@/assets/lp/plans/3.png')
]

export default {
  name: "PlanVerification",
  components: {
    BModal,
    BButton,
    BAlert,
    PlanDetailCard,
    ServiceAreasSection,
    PlansTabsSection
  },
  data() {
    return {
      checkoutCancelAlert: false,
      visibleElements: new Set(),
      plans: [],
      loading: true,
      error: null,
      selectedPlan: null,
      heroImage: require('@/assets/lp/plans/1.jpg'),
      heroBackgroundImage: require('@/assets/lp/1.png'),
      disponibleIcon: require('@/assets/lp/plans/icons/Recurso 25@300x.png'),
      googleMapsUrl: 'https://www.google.com/maps/@32.8915449,-96.9087889,11z/data=!4m2!11m1!2sXs5WthpeGtow6ZlL-JWYW8hYJXOD_A?entry=ttu&g_ep=EgoyMDI1MTIwOS4wIKXMDSoKLDEwMDc5MjA3M0gBUAM%3D',
      locations: [
        {
          name: 'Oak Cliff',
          address: '3434 W Illinois Ave Ste 202, Dallas, Texas, 75211',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'First Street Garland',
          address: '3050 S. First St, Garland, Texas, 75041',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'Fort Worth',
          address: '1303 N Beach St, Fort Worth, Texas, 76111',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'Garland',
          address: '3516 W Walnut St, Garland, Texas, 75042',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'Buckner Blvd Dallas',
          address: '3045 N Buckner Blvd Suite 110, Dallas, Texas, 75228',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'Coit',
          address: '14211 Coit Rd, Dallas, Texas, 75254',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'Denton',
          address: '231 W University Dr #111, Denton, Texas, 76201',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'Irving',
          address: '204 S Nursery Rd #166, Irving, Texas, 75060',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'Josey Lane',
          address: '3065 N Josey Ln Apt 28, Carrollton, Texas, 75007',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        },
        {
          name: 'McKinney',
          address: '811 N McDonald St, McKinney, Texas, 75069',
          phone: '+12148889000',
          email: 'info@tuclinicahispanafamiliar.com',
          hours: '09:00 AM - 10:00 PM',
          days: 'Mon, Tues, Wed, Thur, Fri, Sat, Sun'
        }
      ]
    };
  },
  computed: {
    firstThreePlans() {
      return this.plans.slice(0, 3);
    },
    hasPlans() {
      return Array.isArray(this.plans) && this.plans.length > 0;
    },
    facilitiesModalId() {
      return this.selectedPlan && this.selectedPlan.id 
        ? `facilities-modal-${this.selectedPlan.id}` 
        : 'facilities-modal-default';
    },
    facilitiesModalTitle() {
      return this.selectedPlan && this.selectedPlan.name 
        ? `Clínicas - ${this.selectedPlan.name}` 
        : 'Clínicas';
    },
    proceduresModalId() {
      return this.selectedPlan && this.selectedPlan.id 
        ? `procedures-modal-${this.selectedPlan.id}` 
        : 'procedures-modal-default';
    },
    proceduresModalTitle() {
      return this.selectedPlan && this.selectedPlan.name 
        ? `Beneficios Incluidos - ${this.selectedPlan.name}` 
        : 'Beneficios Incluidos';
    }
  },
  mounted() {
    this.checkCheckoutCancelQuery();
    this.loadPlans();
    this.setupScrollAnimations();
    this.handleHashNavigation();
  },
  watch: {
    '$route.hash'() {
      this.handleHashNavigation();
    },
    '$route.query.checkout'() {
      this.checkCheckoutCancelQuery();
    }
  },
  beforeDestroy() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
  },
  methods: {
    /** Aviso cuando Stripe cancela y vuelve a /planes?checkout=cancelado */
    checkCheckoutCancelQuery() {
      const checkout = this.$route.query && this.$route.query.checkout
      this.checkoutCancelAlert = checkout === 'cancelado' || checkout === 'canceled'
    },
    getPlanCarouselImage(index) {
      return CAROUSEL_IMAGES[index] || CAROUSEL_IMAGES[0];
    },
    /**
     * Carga los planes desde el API
     */
    async loadPlans() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await plansService.getAllPlans();
        
        let plansData = [];
        
        if (response && response.status === 'ok' && response.data && Array.isArray(response.data)) {
          plansData = response.data;
        } else if (Array.isArray(response)) {
          plansData = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          plansData = response.data;
        }
        
        const ordered = enrichAndOrderHmpPlans(plansData)
        this.plans = ordered.length ? ordered : plansData
        
        await this.$nextTick();
        
        this.$nextTick(() => {
          setTimeout(() => {
            const planElements = this.$el.querySelectorAll('.animate-on-scroll, .ddp-visible');
            planElements.forEach(el => {
              if (!this.visibleElements.has(el)) {
                const rect = el.getBoundingClientRect();
                const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
                
                if (isInViewport) {
                  setTimeout(() => {
                    el.classList.add('is-visible');
                    this.visibleElements.add(el);
                  }, 100);
                }
                
                if (this.scrollObserver) {
                  this.scrollObserver.observe(el);
                }
              }
            });
          }, 200);
        });
      } catch (error) {
        console.error('Error loading plans:', error);
        this.error = 'No se pudieron cargar los planes. Por favor, intenta de nuevo más tarde.';
        this.plans = [];
      } finally {
        this.loading = false;
      }
    },
    setupScrollAnimations() {
      const observerOptions = {
        threshold: 0.15,
        rootMargin: '0px 0px -80px 0px'
      };

      if (!this.scrollObserver) {
        this.scrollObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              requestAnimationFrame(() => {
                entry.target.classList.add('is-visible');
                this.visibleElements.add(entry.target);
              });
            } else {
              requestAnimationFrame(() => {
                entry.target.classList.remove('is-visible');
                this.visibleElements.delete(entry.target);
              });
            }
          });
        }, observerOptions);
      }

      const initObserver = () => {
        const animated = this.$el.querySelectorAll('.animate-on-scroll');
        const ddpVisible = this.$el.querySelectorAll('.ddp-visible');
        const elements = [...animated, ...ddpVisible];
        elements.forEach((el) => {
          // Verificar si el elemento ya está en viewport antes de observar
          const rect = el.getBoundingClientRect();
          const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
          
          if (isInViewport) {
            // Si ya está visible, animarlo inmediatamente
            setTimeout(() => {
              el.classList.add('is-visible');
              this.visibleElements.add(el);
            }, 100);
          }
          
          // Observar para animaciones futuras
          this.scrollObserver.observe(el);
        });
      };

      this.$nextTick(() => {
        setTimeout(() => {
          initObserver();
        }, 300);
      });
    },
    showFacilitiesModal(plan) {
      this.selectedPlan = plan;
      this.$nextTick(() => {
        if (plan && plan.id) {
          this.$bvModal.show(`facilities-modal-${plan.id}`);
        }
      });
    },
    showProceduresModal(plan) {
      this.selectedPlan = plan;
      this.$nextTick(() => {
        if (plan && plan.id) {
          this.$bvModal.show(`procedures-modal-${plan.id}`);
        }
      });
    },
    closeFacilitiesModal() {
      if (this.selectedPlan && this.selectedPlan.id) {
        this.$bvModal.hide(`facilities-modal-${this.selectedPlan.id}`);
      }
    },
    closeProceduresModal() {
      if (this.selectedPlan && this.selectedPlan.id) {
        this.$bvModal.hide(`procedures-modal-${this.selectedPlan.id}`);
      }
    },
    handleHashNavigation() {
      this.$nextTick(() => {
        const hash = this.$route.hash ? this.$route.hash.replace('#', '') : window.location.hash.replace('#', '');
        if (hash) {
          const element = document.getElementById(hash);
          if (element) {
            setTimeout(() => {
              const offset = 100; // Offset para el header sticky
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - offset;
              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
            }, 800);
          }
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../styles/variables';

/* Animaciones de scroll */
.animate-on-scroll {
  opacity: 0;
  transition: opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1), 
              transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: opacity, transform;

  &.fade-in-up {
    transform: translateY(40px);
  }

  &.fade-in-left {
    transform: translateX(-40px);
  }

  &.fade-in-right {
    transform: translateX(40px);
  }

  &.fade-in-scale {
    transform: scale(0.9);
  }

  &.is-visible {
    opacity: 1;
    transform: translate(0) scale(1) !important;
  }
  
  &:not(.is-visible) {
    opacity: 0;
    
    &.fade-in-up {
      transform: translateY(40px);
    }
    
    &.fade-in-left {
      transform: translateX(-40px);
    }
    
    &.fade-in-right {
      transform: translateX(40px);
    }
    
    &.fade-in-scale {
      transform: scale(0.9);
    }
  }
}

/* Animaciones ddp-visible para plans carousel */
.ddp-visible {
  opacity: 0;
  transition: opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1),
              transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  &.fade-in-up {
    transform: translateY(18px);

    &.is-visible {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &.is-visible {
    opacity: 1;
    transform: translate(0);
  }
}

.plan-verification-page {
  overflow-x: hidden;

  .hero-section {
    overflow-x: hidden;
    min-height: 60vh;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    padding: 80px 0;
    color: white;
    display: flex;
    align-items: center;

    .hero-title {
      font-size: clamp(2.5rem, 6.5vw, 4.5rem);
      font-weight: 700;
      margin-bottom: 1.5rem;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      line-height: 1.2;
    }

    .hero-subtitle {
      font-size: clamp(1.15rem, 2.5vw, 1.75rem);
      margin-bottom: 2rem;
      opacity: 0.95;
      line-height: 1.6;
    }

    .btn-hero-cta {
      background: $landing-secondary-color;
      color: $landing-primary-color;
      padding: 14px 32px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 1.5rem;
      text-transform: uppercase;
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      transition: all 0.3s ease;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
      border: none;

      i {
        color: $landing-primary-color !important;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(34, 207, 224, 0.4);
        background: darken($landing-secondary-color, 8%);
        color: $landing-primary-color;
        text-decoration: none;

        i {
          color: $landing-primary-color !important;
        }
      }
    }

    @media (max-width: 768px) {
      min-height: 50vh;
      padding: 60px 1rem;
    }
  }

  .plans-carousel-section {
    padding: 80px 0;
    background: $landing-bg-light;

    .plan-image-card {
      background: transparent;
      border-radius: 15px;
      overflow: hidden;
      box-shadow: none;
      transition: all 0.3s ease;
      height: 100%;
      display: flex;
      flex-direction: column;

      &:hover {
        transform: translateY(-4px);
      }

      .plan-image-top.plan-image-clickable {
        cursor: pointer;
      }

      .plan-image-top {
        position: relative;
        aspect-ratio: 3/4;
        overflow: hidden;
        border-radius: 15px 15px 0 0;

        .plan-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .plan-image-overlay {
          position: absolute;
          bottom: 1rem;
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
        }

        .btn-lo-necesito {
          background: white;
          color: $landing-primary-color;
          border: 2px solid $landing-primary-color;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 1.15rem;
          text-transform: uppercase;
          text-decoration: none;
          transition: all 0.3s ease;

          &:hover {
            background: $landing-primary-color;
            color: white;
            text-decoration: none;
          }
        }
      }

      .plan-card-body {
        padding: 1.25rem 1.5rem;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        min-height: 0;
      }

      .plan-name {
        font-size: 1.75rem;
        font-weight: 600;
        color: $landing-text-dark;
        margin: 0 0 0.5rem;
      }

      .plan-price-large {
        font-size: 4rem;
        font-weight: 700;
        color: $landing-primary-color;
        margin-bottom: 0.25rem;

        &.price-secondary {
          color: $landing-secondary-color;
        }
      }

      .plan-savings-line {
        margin-bottom: 0.5rem;
        font-size: 1.6rem;

        .plan-original {
          color: $landing-primary-color;
          text-decoration: line-through;
          margin-right: 0.5rem;
        }

        .plan-percent {
          color: $landing-primary-color;
        }
      }

      .plan-savings-spacer {
        visibility: hidden;
        font-size: 1.6rem;
        line-height: 1.5;
        margin-bottom: 0.5rem;
        min-height: 2.4em;
      }

      .btn-plan-available {
        margin-top: auto;
        padding-top: 1rem;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        background: white;
        color: $landing-primary-color;
        border: 1px solid $landing-border-light;
        padding: 12px 28px;
        border-radius: 8px;
        font-weight: 600;
        font-size: 1.2rem;
        text-transform: uppercase;
        text-decoration: none;
        transition: all 0.3s ease;

        .btn-disponible-icon {
          width: 24px;
          height: 24px;
          object-fit: contain;
        }

        &:hover {
          background: $landing-bg-light;
          color: $landing-primary-color;
          border-color: $landing-primary-color;
          text-decoration: none;
        }
      }
    }
  }
  
  .plans-intro-section.plans-intro-hero {
    overflow-x: hidden;
    min-height: 600px;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    padding: 80px 0;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;

    .container {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      height: 100%;
    }

    .row {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      width: 100%;
    }

    .plans-intro-title {
      font-size: 4rem;
      font-weight: 700;
      margin-bottom: 1.5rem;
      color: white;
      text-align: left;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    .plans-intro-subtitle {
      font-size: 1.8rem;
      opacity: 1;
      color: white;
      font-weight: 700;
      text-align: left;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      line-height: 1.5;
      margin: 0;
    }

    @media (max-width: 768px) {
      min-height: 420px;
      padding: 100px 24px;
      background-size: cover;
      background-position: center;

      .plans-intro-title {
        font-size: 2.5rem;
      }

      .plans-intro-subtitle {
        font-size: 1.2rem;
      }
    }
  }

  .plans-section {
    padding: 80px 0;
    background: #F3F6F9;
    
    .section-title {
      font-size: 3.5rem;
      font-weight: 700;
      color: $landing-primary-color;
      margin-bottom: 1.5rem;
    }
    
    .section-subtitle {
      color: $landing-text-dark;
      font-size: 1.4rem;
      font-weight: 500;
    }
    
    .plans-comparison-table {
      background: $landing-bg-white;
      border-radius: 15px;
      overflow: hidden;
      box-shadow: 0 5px 20px $landing-primary-rgba-08;
      
      thead {
        background: $landing-primary-gradient;
        color: white;
        
        th {
          border: none;
          padding: 1.5rem;
          font-weight: 600;
          
          &.benefit-column {
            width: 30%;
          }
          
          &.plan-column {
            text-align: center;
            
            .plan-header-cell {
              h4 {
                font-size: 1.2rem;
                margin-bottom: 0.5rem;
                font-weight: 600;
              }
              
              .plan-price {
                font-size: 1.8rem;
                font-weight: 700;
                margin: 0.5rem 0;
              }

              .plan-validity {
                margin-top: 0.5rem;
                opacity: 0.9;
                font-size: 0.9rem;
              }
            }
          }
        }
      }
      
      tbody {
          tr {
            &:nth-child(even) {
              background: $landing-bg-light;
            }
          
          td {
            padding: 1.5rem;
            vertical-align: middle;
            
            &.benefit-name {
              font-weight: 700;
              color: $landing-text-dark;
              font-size: 1.2rem;
              
              i {
                color: $landing-primary-color;
                width: 20px;
                font-size: 1.1rem;
              }
            }
            
            .info-compact {
              display: flex;
              flex-direction: column;
              align-items: center;
              
              .info-number {
                font-size: 2rem;
                font-weight: 700;
                color: $landing-primary-color;
                line-height: 1;
              }
              
              .info-label {
                font-size: 0.85rem;
                color: $landing-text-medium;
                margin-top: 0.25rem;
                text-transform: lowercase;
              }
              
              .btn-link {
                color: $landing-primary-color;
                font-size: 0.85rem;
                text-decoration: none;
                
                &:hover {
                  color: $landing-secondary-color;
                  text-decoration: underline;
                }
              }
            }
            
            .validity-badge {
              display: inline-block;
              padding: 0.5rem 1rem;
              background: rgba(232, 244, 248, 1);
              color: $landing-secondary-color;
              border-radius: 20px;
              font-weight: 600;
            }
            
            .badge-lg {
              padding: 0.5rem 1rem;
              font-size: 0.9rem;
            }
          }
        }
      }
    }
  }
  
  /* Misma sección de planes que Home para aspecto idéntico */
  .detailed-plans-section {
    padding: 80px 0;
    background: #e6f1f3;

    .section-header {
      margin-bottom: 2.5rem;
      max-width: 760px;
      margin-left: auto;
      margin-right: auto;

      .section-title {
        color: #0d2b45;
        font-size: clamp(1.85rem, 3.5vw, 2.75rem);
        font-weight: 800;
        margin-bottom: 0.75rem;
      }

      .section-subtitle {
        color: #4a5d6b;
        font-size: 1.15rem;
        font-weight: 500;
        line-height: 1.55;
      }
    }

    .plans-five-row {
      display: grid;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      gap: 1rem;
      align-items: stretch;
    }

    .plans-five-col {
      min-width: 0;
      display: flex;
      flex-direction: column;
    }

    @media (max-width: 1199.98px) {
      .plans-five-row {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
    }

    @media (max-width: 767.98px) {
      .plans-five-row {
        grid-template-columns: 1fr;
      }
    }

    .plan-card-wrapper {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .detailed-plan-card {
      background: linear-gradient(135deg, #ffffff 0%, #F8F9FA 100%);
      border: 2px solid #0d2b45;
      border-radius: 15px;
      padding: 2rem;
      position: relative;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      flex: 1;
      display: flex;
      flex-direction: column;

      &.plan-with-gradient {
        background: #ffffff;
        border-color: #0d2b45;

        .plan-header {
          background: linear-gradient(to right, #123a5c 0%, #22cfe0 100%);
          border-radius: 15px 15px 0 0;
          margin: -2rem -2rem 2rem -2rem;
          padding: 2rem 2rem 1.5rem 2rem;
          border-bottom: 3px solid #0d2b45;
          color: white;

          .plan-name {
            color: white;
            font-size: 2.5rem;
            margin-bottom: 1.5rem;
            margin-top: 1rem;
          }

          .plan-price-large .currency,
          .plan-price-large .amount {
            color: white;
          }
          .plan-price-large .period {
            color: rgba(255, 255, 255, 0.9);
          }
          .plan-meta .meta-item {
            color: rgba(255, 255, 255, 0.9);
          }
        }
      }

      &:hover {
        border-color: #0d2b45;
        box-shadow: 0 15px 40px rgba(13, 43, 69, 0.85);
        transform: translateY(-8px);
        background: linear-gradient(135deg, #ffffff 0%, #E8F4F8 100%);
      }

      .plan-header {
        text-align: center;
        margin-bottom: 2rem;
        margin-top: 2rem;
        padding-bottom: 1.5rem;
        padding-top: 2rem;
        border-bottom: 3px solid #0d2b45;

        .plan-name {
          font-size: 2.5rem;
          font-weight: 700;
          color: #2D4A4D;
          margin-bottom: 1.5rem;
          margin-top: 1rem;
        }

        .plan-price-large {
          margin-bottom: 1rem;
          display: flex;
          align-items: baseline;
          justify-content: center;

          .currency {
            font-size: 3.5rem;
            vertical-align: baseline;
            color: #0d2b45;
            line-height: 1;
          }

          .amount {
            font-size: 7rem;
            font-weight: 700;
            color: #0d2b45;
            line-height: 1;
          }

          .period {
            font-size: 2rem;
            color: #5A6D70;
            line-height: 1;
            vertical-align: baseline;
          }
        }

        .plan-meta {
          display: flex;
          justify-content: center;
          gap: 1.5rem;
          flex-wrap: wrap;

          .meta-item {
            color: #5A6D70;
            font-size: 0.9rem;

            i {
              color: #0d2b45;
            }
          }
        }
      }

      .plan-body .plan-statistics {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1.5rem;
        margin-bottom: 1.5rem;

        .stat-item {
          text-align: center;
          padding: 1.5rem;
          background: #F8F9FA;
          border-radius: 10px;
          min-height: 180px;

          .stat-number {
            font-size: 2.5rem;
            font-weight: 700;
            color: #0d2b45;
          }

          .stat-label {
            font-size: 0.9rem;
            color: #5A6D70;
          }

          .btn-outline-primary {
            border-color: #0d2b45;
            color: #0d2b45;
          }
        }
      }

      .plan-badge {
        position: absolute;
        top: -15px;
        left: 50%;
        transform: translateX(-50%);
        background: #FFFFFF;
        color: #0d2b45;
        border: 2px solid #0d2b45;
        padding: 10px 24px;
        border-radius: 8px;
        font-size: 1.1rem;
        font-weight: 700;
        z-index: 2;
      }
    }

    .plan-action-btn {
      background: linear-gradient(135deg, #0d2b45 0%, #22cfe0 100%) !important;
      color: white !important;
      border: none !important;
      padding: 16px 32px !important;
      font-size: 1.2rem !important;
      border-radius: 12px !important;
      margin-top: 1rem;
      width: 100%;
      font-weight: 700 !important;
      transition: all 0.3s ease !important;
    }

    .plan-action-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(13, 43, 69, 0.4) !important;
    }
  }
  
  /* Modales de detalle (Clínicas y Procedimientos) - UI/UX profesional */
  .detail-modal-content {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(13, 43, 69, 0.15);
    border: none;
  }

  .detail-modal-body {
    padding: 0;
    max-height: 65vh;

    &--facilities {
      max-height: 60vh;
    }

    &--procedures {
      max-height: 65vh;
    }

    &--scroll {
      overflow-y: auto;
    }
  }

  .detail-modal-footer {
    padding: 1rem 1.5rem;
    background: #F8F9FA;
    border-top: 1px solid #E9ECEF;
    justify-content: flex-end;
  }

  .detail-modal__btn-close {
    min-width: 140px;
    font-weight: 600;
    border-radius: 10px;
    padding: 0.6rem 1.25rem;
  }

  .detail-modal {
    .detail-modal__header {
      padding: 1.75rem 1.5rem;
      text-align: center;
      color: white;

      &--primary {
        background: linear-gradient(135deg, #0d2b45 0%, #2a7a9e 100%);
      }

      &--secondary {
        background: linear-gradient(135deg, #0d7a6e 0%, #22cfe0 100%);
      }
    }

    .detail-modal__icon-wrap {
      width: 56px;
      height: 56px;
      margin: 0 auto 1rem;
      background: rgba(255, 255, 255, 0.25);
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.5rem;
      animation: modal-icon-in 0.5s ease;
    }

    .detail-modal__title {
      font-size: 1.35rem;
      font-weight: 700;
      margin: 0 0 0.35rem;
      color: white;
      line-height: 1.3;
    }

    .detail-modal__subtitle {
      font-size: 0.95rem;
      margin: 0;
      opacity: 0.95;
      font-weight: 400;
      line-height: 1.4;
    }

    .detail-modal__body {
      padding: 1.5rem;
      background: #fff;

      &--scroll {
        max-height: 50vh;
        overflow-y: auto;
      }
    }
  }

  /* Lista de clínicas - filas limpias */
  .facility-list {
    display: flex;
    flex-direction: column;
  }

  .facility-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #eee;
    transition: background 0.25s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: rgba(13, 43, 69, 0.06);
    }

    &--animate {
      opacity: 0;
      animation: modal-row-in 0.35s ease forwards;
    }
  }

  .facility-row__num {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    background: $landing-primary-color;
    color: #fff;
    border-radius: 50%;
    font-size: 0.9rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .facility-row__icon {
    color: $landing-primary-color;
    font-size: 1.1rem;
    flex-shrink: 0;
  }

  .facility-row__name {
    font-size: 1.05rem;
    font-weight: 500;
    color: #2d4a4d;
    flex: 1;
  }

  @keyframes modal-card-in {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes modal-row-in {
    from {
      opacity: 0;
      transform: translateX(-8px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes modal-icon-in {
    0% {
      opacity: 0;
      transform: scale(0.8);
    }
    60% {
      transform: scale(1.05);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }

  /* Grid de beneficios - cards estilo limpio (icono + texto) */
  .benefits-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 1rem;
  }

  .benefit-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e5eaee;
    transition: all 0.3s ease;

    &:hover {
      border-color: #9e9e9e;
    }

    &--animate {
      opacity: 0;
      animation: benefit-card-in 0.4s ease forwards;
    }
  }

  @keyframes benefit-card-in {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .benefit-card__icon-wrap {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border: 1px solid #e5eaee;
    border-radius: 10px;

    i {
      font-size: 1.25rem;
      color: $landing-primary-color;
    }
  }

  .benefit-card__content {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
  }

  .benefit-card__name {
    font-size: 1rem;
    font-weight: 700;
    color: $landing-primary-color;
    line-height: 1.3;
  }

  .benefit-card__qty {
    font-size: 0.9rem;
    color: #5a6d70;
    line-height: 1.35;
  }

  .benefit-card__tag {
    font-size: 0.75rem;
    color: #5a6d70;
    margin-top: 0.25rem;
  }

  /* Estado vacío */
  .detail-modal__empty {
    text-align: center;
    padding: 2.5rem 1.5rem;
  }

  .detail-modal__empty-icon {
    width: 72px;
    height: 72px;
    margin: 0 auto 1.25rem;
    background: #F0F4F5;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.75rem;
    color: #8a9a9c;
  }

  .detail-modal__empty-title {
    font-size: 1.15rem;
    font-weight: 600;
    color: #2D4A4D;
    margin: 0 0 0.5rem;
  }

  .detail-modal__empty-text {
    font-size: 0.95rem;
    color: #5A6D70;
    margin: 0;
    line-height: 1.5;
    max-width: 360px;
    margin-left: auto;
    margin-right: auto;
  }

  .marketing-benefits-list {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      display: flex;
      flex-direction: column;
      gap: 0.2rem;
      padding: 0.85rem 1rem;
      margin-bottom: 0.65rem;
      background: #f8fafc;
      border-radius: 10px;
      border-left: 3px solid #22cfe0;

      b {
        color: #0d2b45;
        font-size: 0.98rem;
      }

      span {
        color: #5a6d70;
        font-size: 0.9rem;
        line-height: 1.4;
      }
    }
  }
}
</style>
